import React, { useMemo } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { SiteConfigManager } from '@atlas/core';
import { CASINO_TILE_TYPES } from '@atlas/ui';

import { GAME_TILE_VIEW_HEIGHT } from '../constants';

import { CasinoRecommendedGames } from './CasinoRecommendedGames';
import { EmptyState } from './EmptyState';
import InfoGame from './InfoGame';
import { InfoGamesSlider } from './InfoGamesSlider';

const LOAD_MORE_BTN_HEIGHT = 96;

const InfoGamesList = React.forwardRef(
  (
    {
      games,
      hasLoadMore,
      isDetailPage = false,
      isFavorites = false,
      isFullWidth = false,
      loading,
      setActiveItem,
      setSwiper,
      showSlider = false,
      sliderSettings = {},
    },
    ref,
  ) => {
    const casinoGameTileType =
      SiteConfigManager.getConfig('config.config.general.desktopCasinoGameTileView') ||
      CASINO_TILE_TYPES.TYPE_5;

    const isRenderNotEmptyLayout = useMemo(() => {
      return games.length > 0;
    }, [games]);

    const renderEmptyState = useMemo(() => {
      return isFavorites ? (
        <EmptyState isFavorites={isFavorites} loading={loading} />
      ) : (
        <CasinoRecommendedGames loading={loading} setActiveItem={setActiveItem} />
      );
    }, [isFavorites, loading, setActiveItem]);

    const casinoGamesClasses = cn('casino__games', {
      [`casino__games--size-${sliderSettings?.size?.toLowerCase()}`]: sliderSettings.size,
      'casino__games--detail-page': isDetailPage,
      'casino__games--full-width': isFullWidth,
    });

    const casinoGamesStyle = useMemo(
      () =>
        hasLoadMore
          ? {
              marginBottom: `-${GAME_TILE_VIEW_HEIGHT[casinoGameTileType] - LOAD_MORE_BTN_HEIGHT}px`,
            }
          : {},
      [casinoGameTileType, hasLoadMore],
    );

    return (
      <div className="casino__games--list" ref={ref}>
        {isRenderNotEmptyLayout ? (
          showSlider ? (
            <InfoGamesSlider
              games={games}
              isFavorites={isFavorites}
              setSwiper={setSwiper}
              sliderSettings={sliderSettings}
            />
          ) : (
            <div className={casinoGamesClasses} style={casinoGamesStyle}>
              {games.map(game => (
                <InfoGame
                  game={game}
                  gameItemClass="game"
                  isFavorites={isFavorites}
                  key={game.id ?? game.gameRelationId}
                  size={sliderSettings.size}
                />
              ))}
            </div>
          )
        ) : (
          renderEmptyState
        )}
      </div>
    );
  },
);

InfoGamesList.propTypes = {
  games: PropTypes.array.isRequired,
  hasLoadMore: PropTypes.bool,
  isDetailPage: PropTypes.bool,
  isFavorites: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  loading: PropTypes.bool,
  setActiveItem: PropTypes.func,
  setSwiper: PropTypes.func,
  showSlider: PropTypes.bool,
  sliderSettings: PropTypes.object,
};

export default InfoGamesList;
